import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useBack } from "@/components/BackButton";
import { ArrowLeft, Play, Plus, SkipBack, Film, Sparkles, Crop, Captions, Sticker, Music2, Zap, Send } from "lucide-react";
import { api, mediaUrl, formatApiError, fmtTimePrecise } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { StatusBadge } from "@/components/VideoCard";
import { InlineLoader } from "@/components/LoadingScreen";
import { Player } from "@/components/editor/Player";
import { Timeline } from "@/components/editor/Timeline";
import { HighlightList } from "@/components/editor/HighlightList";
import { AnalyzePanel } from "@/components/editor/AnalyzePanel";
import { ExportPanel, makeClipBody } from "@/components/editor/ExportPanel";
import { SubtitlesPanel } from "@/components/editor/SubtitlesPanel";
import { AiEditBox } from "@/components/editor/AiEditBox";
import { toFree } from "@/components/editor/StickerLayer";
import { ProPanel } from "@/components/editor/ProPanel";
import { TemplatesPanel, tplToCfg } from "@/components/editor/TemplatesPanel";
import { VariantsPanel } from "@/components/editor/VariantsPanel";
import { applyFace } from "@/components/editor/FacePanel";
import { useEditorShortcuts, ShortcutsButton, ShortcutsDialog } from "@/components/editor/Shortcuts";

const TABS = [["moments", Sparkles], ["format", Crop], ["subs", Captions], ["stickers", Sticker], ["audio", Music2], ["fx", Zap], ["export", Send]];

const ghost = "cx-ghost rounded-full px-3 py-2 text-xs flex items-center gap-1.5";

export default function Editor() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const clipId = sp.get("clip");
  const nav = useNavigate();
  const { back } = useBack();
  const { t } = useI18n();
  const vref = useRef(null);
  const stopAt = useRef(null);
  const [video, setVideo] = useState(null);
  const [current, setCurrent] = useState(0);
  const [range, setRange] = useState({ start: 0, end: 15 });
  const [active, setActive] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [tab, setTab] = useState(() => localStorage.getItem("clipx_editor_tab") || "moments");
  const [selSticker, setSelSticker] = useState(null);
  const pickTab = (k) => { localStorage.setItem("clipx_editor_tab", k); setTab(k); };
  const [cfg, setCfg] = useState({ platform: "tiktok", aspect: "9:16", fit: "blur", crop_x: 0.5, crop_y: 0.5, zoom: 1, quality: "1080", title: "", description: "", hashtags: "", effects: ["watermark", "fadeout"], hook: "", stickers: [], sfx: [], impact: null, subs: true, sub_style: localStorage.getItem("clipx_sub_style") || "tiktok", sub_pos: "auto", words: null, words_key: "", pro: null, auto_face: false, hq: true });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/videos/${id}`);
      setVideo((prev) => {
        if (!prev && data.duration) setRange({ start: 0, end: Math.min(15, data.duration) });
        return data;
      });
    } catch (e) { toast.error(formatApiError(e)); nav("/dashboard"); }
  }, [id, nav]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (video?.status !== "analyzing") return;
    const i = setInterval(load, 4000);
    return () => clearInterval(i);
  }, [video?.status, load]);

  const cfgAspect = useRef("9:16");
  useEffect(() => { cfgAspect.current = cfg.aspect; }, [cfg.aspect]);
  const booted = useRef(false);
  useEffect(() => {
    if (!video || booted.current || ["uploading", "processing", "error"].includes(video.status)) return;
    booted.current = true;
    (async () => {
      const tpls = await api.get("/templates").then((r) => r.data).catch(() => []);
      const def = tpls.find((x) => x.is_default);
      let fc = video.facecam;
      if (fc == null) {
        fc = await api.post(`/videos/${video.id}/facecam`, {}).then((r) => r.data.facecam).catch(() => ({ found: false }));
        setVideo((v) => ({ ...v, facecam: fc }));
      }
      if (def) { setCfg((c) => tplToCfg(c, def.settings, fc)); toast(t("tpl.auto_applied", { n: def.name })); }
      else if (fc?.found) { setCfg((c) => applyFace(c, fc)); if (cfgAspect.current === "9:16" && !clipId) toast.success(t("face.applied")); }
      if (clipId) {
        const src = (await api.get("/clips", { params: { video_id: video.id } }).then((r) => r.data).catch(() => [])).find((x) => x.id === clipId);
        if (src) {
          setRange({ start: src.start, end: src.end });
          const keep = ["fit", "crop_x", "crop_y", "zoom", "split_ratio", "cam_rect", "cam_zoom", "cam_x", "cam_y", "music", "quality", "effects", "stickers", "sfx", "subs", "sub_style", "sub_pos", "sub_x", "sub_y", "sub_scale", "pro", "hq", "auto_face"];
          const style = Object.fromEntries(keep.filter((k) => src[k] != null).map((k) => [k, src[k]]));
          setCfg((c) => ({ ...c, ...style, title: src.title || "", description: src.description || "", hashtags: (src.hashtags || []).join(" "),
            platform: src.platform || c.platform, aspect: src.aspect || c.aspect, hook: src.hook || "", impact: src.impact ?? null }));
          if (vref.current) vref.current.currentTime = src.start;
          setCurrent(src.start);
          toast.success(t("ed.reedit_loaded"));
        }
      }
    })();
  }, [video, t, clipId]);
  const prevStatus = useRef(null);
  useEffect(() => {
    if (prevStatus.current === "analyzing" && video?.status !== "analyzing" && video?.highlights.length) toast.success(t("ed.detected", { n: video.highlights.length }));
    prevStatus.current = video?.status;
  }, [video?.status, video?.highlights.length, t]);

  const seek = (s) => { if (vref.current) vref.current.currentTime = s; setCurrent(s); };
  const onTime = (s) => {
    setCurrent(s);
    if (stopAt.current != null && s >= stopAt.current) { vref.current.pause(); stopAt.current = null; }
  };
  const [help, setHelp] = useState(false);
  useEditorShortcuts({
    vref, seek, duration: video?.duration || 0, pickTab: (k) => pickTab(k), openHelp: () => setHelp(true), save: () => addManual(),
    setStart: () => { const c = vref.current?.currentTime ?? current; setRange((r) => ({ ...r, start: Math.min(c, r.end - 0.5) })); toast(t("sc.start_set")); },
    setEnd: () => { const c = vref.current?.currentTime ?? current; setRange((r) => ({ ...r, end: Math.max(c, r.start + 0.5) })); toast(t("sc.end_set")); },
  });
  const preview = () => { seek(range.start); stopAt.current = range.end; vref.current?.play(); };

  const pick = (h) => {
    setActive(h);
    setRange({ start: h.start, end: h.end });
    setCfg((c) => ({ ...c, title: h.title || h.label, description: h.reason, hashtags: (h.hashtags || []).join(" "), hook: h.hook || "",
      effects: h.effects?.length ? Array.from(new Set([...h.effects, "watermark"])) : c.effects,
      impact: h.impact ?? null, stickers: (h.stickers || []).map((id, i) => toFree({ id, t: +((h.impact || 0) + i * 0.8).toFixed(2), pos: ["center", "top"][i] || "bottom" })),
      sfx: (h.sfx || []).map((id, i) => ({ id, t: +((h.impact || 0) + i * 0.3).toFixed(2) })) }));
    seek(h.start);
  };

  const addManual = async () => {
    try {
      const { data } = await api.post(`/videos/${id}/highlights`, { start: range.start, end: range.end, label: cfg.title || t("ed.manual") });
      toast.success(t("ed.moment_added"));
      setActive(data);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const delHighlight = async (h) => {
    await api.delete(`/videos/${id}/highlights/${h.id}`);
    if (active?.id === h.id) setActive(null);
    load();
  };

  if (!video) return <div className="p-24 grid place-items-center" data-testid="editor-loading"><InlineLoader label={t("ed.loading")} /></div>;
  const duration = video.duration || 1;

  return (
    <div className="px-4 sm:px-6 lg:px-6 2xl:px-10 py-6 sm:py-8 lg:py-4 pb-28 lg:pb-6" data-testid="editor-page">
      <div className="flex flex-wrap items-center justify-between gap-4 cx-rise">
        <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
          <button data-testid="editor-back-btn" onClick={back} className="cx-metal rounded-lg p-2.5 shrink-0"><ArrowLeft className="w-4 h-4" /></button>
          <div className="min-w-0">
            <div className="cx-label truncate">{t("ed.label")} · {video.game || t("ed.unknown_game")}</div>
            <h1 className="font-display text-xl sm:text-3xl font-bold text-white truncate" data-testid="editor-video-title">{video.title}</h1>
          </div>
          <StatusBadge status={video.status} />
        </div>
        <button data-testid="editor-view-clips-btn" onClick={() => nav("/clips")} className="cx-ghost rounded-full px-4 py-2 text-sm flex items-center gap-2"><Film className="w-4 h-4" /> {t("nav.clips")}</button>
      </div>

      <ShortcutsDialog open={help} onOpenChange={setHelp} />
      <div className="flex flex-col gap-5 mt-6 lg:mt-5 lg:grid lg:grid-cols-[96px_minmax(0,1fr)_400px] xl:grid-cols-[104px_minmax(0,1fr)_440px] lg:grid-rows-[minmax(0,1fr)_auto] lg:gap-4 lg:h-[calc(100vh-200px)] lg:min-h-[600px]" data-testid="editor-workspace">
        <div className="sticky top-[75px] md:top-0 z-30 bg-[#05060c] -mx-4 sm:-mx-6 px-4 sm:px-6 pb-2 pt-1 lg:static lg:m-0 lg:p-0 lg:bg-transparent min-w-0 lg:col-start-2 lg:row-start-1 lg:min-h-0 lg:flex lg:flex-col" data-testid="editor-player-wrap">
          <Player ref={vref} src={mediaUrl(`/videos/${id}/stream`)} video={video} cfg={cfg} setCfg={setCfg} onTime={onTime} current={current} range={range} playing={playing} onPlayState={setPlaying} selected={selSticker} setSelected={setSelSticker}
            getBody={() => makeClipBody(video, range, cfg, active)} />
        </div>
        <div className="cx-card rounded-xl p-4 min-w-0 lg:col-span-3 lg:row-start-2" data-testid="editor-timeline-card">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <div className="flex flex-wrap items-center gap-2">
              <button data-testid="preview-selection-btn" onClick={preview} className="cx-btn rounded-full px-4 py-2 text-xs font-semibold flex items-center gap-1.5"><Play className="w-3.5 h-3.5" /> {t("ed.preview")}</button>
              <button data-testid="set-start-btn" onClick={() => setRange((r) => ({ ...r, start: Math.min(current, r.end - 0.5) }))} className={ghost}><SkipBack className="w-3.5 h-3.5" /> {t("ed.start_here")}</button>
              <button data-testid="set-end-btn" onClick={() => setRange((r) => ({ ...r, end: Math.max(current, r.start + 0.5) }))} className={ghost}>{t("ed.end_here")}</button>
              <button data-testid="add-manual-highlight-btn" onClick={addManual} className={ghost}><Plus className="w-3.5 h-3.5" /> {t("ed.save_moment")}</button>
            </div>
            <div className="flex items-center gap-3">
              <ShortcutsButton onClick={() => setHelp(true)} />
              <span className="font-mono text-xs text-slate-400" data-testid="playhead-time">{fmtTimePrecise(current)} / {fmtTimePrecise(duration)}</span>
            </div>
          </div>
          <Timeline duration={duration} current={current} range={range} setRange={setRange} highlights={video.highlights} activeId={active?.id} onSeek={seek} onPickHighlight={pick} />
        </div>
        <nav className="hidden lg:flex lg:col-start-1 lg:row-start-1 flex-col gap-1.5 rounded-xl cx-metal-rail p-1.5 overflow-y-auto min-h-0" data-testid="editor-tools-rail">
          {TABS.map(([k, Icon]) => (
            <button key={k} onClick={() => pickTab(k)} data-testid={`editor-rail-${k}`} title={t(`ed.tab_${k}`)}
              className={`flex flex-col items-center gap-1.5 rounded-lg py-3 px-1 text-[10px] font-semibold leading-tight text-center transition-colors ${tab === k ? "cx-metal cx-metal-active text-white" : "text-slate-400 hover:text-white hover:bg-white/[0.04]"}`}>
              <Icon className="w-5 h-5" />{t(`ed.tab_${k}`)}
            </button>
          ))}
        </nav>
        <div className="min-w-0 space-y-4 lg:col-start-3 lg:row-start-1 lg:overflow-y-auto lg:min-h-0 lg:pr-1 cx-scroll" data-testid="editor-settings-column">
          <div className="grid grid-cols-7 gap-1 rounded-xl cx-metal-rail p-1 sticky top-[calc(75px+34vh+58px)] md:top-[calc(52vh+60px)] z-20 bg-[#05060c]/95 backdrop-blur lg:hidden" data-testid="editor-tabs">
            {TABS.map(([k, Icon]) => (
              <button key={k} onClick={() => pickTab(k)} data-testid={`editor-tab-${k}`} title={t(`ed.tab_${k}`)}
                className={`flex flex-col items-center gap-1 rounded-lg py-2 text-[10px] font-semibold transition-colors ${tab === k ? "cx-metal cx-metal-active text-white" : "text-slate-400 hover:text-white"}`}>
                <Icon className="w-4 h-4" /><span className="max-w-full px-0.5 leading-tight text-center break-normal text-[9px] sm:text-[10px]">{t(`ed.tab_${k}`)}</span>
              </button>
            ))}
          </div>
          <div className="hidden lg:flex items-center gap-2 px-1 pt-1" data-testid="editor-settings-title">
            {(() => { const Icon = Object.fromEntries(TABS)[tab] || Sparkles; return <Icon className="w-4 h-4 text-[#3ad7ff]" />; })()}
            <span className="font-display text-sm font-semibold text-white">{t(`ed.tab_${tab}`)}</span>
          </div>
          {tab === "moments" && (
            <div className="space-y-4" data-testid="editor-pane-moments">
              <TemplatesPanel video={video} cfg={cfg} setCfg={setCfg} />
              <VariantsPanel video={video} range={range} cfg={cfg} active={active} />
              <ProPanel cfg={cfg} setCfg={setCfg} />
              <AiEditBox video={video} cfg={cfg} setCfg={setCfg} range={range} setRange={setRange} active={active} />
              <AnalyzePanel video={video} onStarted={load} />
              <div className="cx-card rounded-xl p-4 sm:p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="cx-label">{t("ed.suggested")}</div>
                  <span className="font-mono text-xs text-slate-500">{video.highlights.length}</span>
                </div>
                <HighlightList highlights={video.highlights} activeId={active?.id} onPick={pick} onDelete={delHighlight} />
              </div>
            </div>
          )}
          {tab === "fx" && <ProPanel cfg={cfg} setCfg={setCfg} />}
          {tab === "subs" && <SubtitlesPanel video={video} range={range} cfg={cfg} setCfg={setCfg} />}
          <ExportPanel video={video} range={range} cfg={cfg} setCfg={setCfg} active={active} onExported={load} current={current}
            tab={["moments", "subs"].includes(tab) ? "none" : tab} onStickerAdded={(i) => { vref.current?.pause(); setSelSticker(i); }} onStickerRemoved={() => setSelSticker(null)} onFacecam={(fc) => setVideo((v) => ({ ...v, facecam: fc }))} />
        </div>
      </div>
    </div>
  );
}
